import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, register } = useAuth();
  // Honor `?mode=register` (from collaborator-invite emails) so the page
  // lands directly on the sign-up form instead of the sign-in form.
  const [isLogin, setIsLogin] = useState(searchParams.get('mode') !== 'register');
  // Same-origin path to return to after sign-in (e.g. an officials invite link).
  const redirectTo = (() => { const r = searchParams.get('redirect'); return r && r.startsWith('/') && !r.startsWith('//') ? r : null; })();
  const [loading, setLoading] = useState(false);
  const [showVerificationBanner, setShowVerificationBanner] = useState(false);
  const [pendingClaimedEventId, setPendingClaimedEventId] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);
  const [formData, setFormData] = useState({
    email: searchParams.get('email') || '',
    password: '',
    confirmPassword: '',
    name: ''
  });

  // If the querystring changes (e.g. user clicks a fresh invite link in
  // the same tab) keep the form + mode in sync.
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam && emailParam !== formData.email) {
      setFormData((f) => ({ ...f, email: emailParam }));
    }
    if (searchParams.get('mode') === 'register' && isLogin) {
      setIsLogin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Verification email sent!');
      } else {
        toast.error(data.detail || 'Failed to resend verification email');
      }
    } catch (error) {
      toast.error('Failed to resend verification email');
    } finally {
      setResendingVerification(false);
    }
  };

  const handleGoogleSignIn = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      toast.error('Google sign-in is not configured.');
      return;
    }
    // Same-origin redirect URI — must match what's registered in Google Cloud
    // Console for THIS deployment (production OR preview). DO NOT hardcode
    // or add fallbacks; the dynamic origin keeps preview/production aligned.
    const redirectUri = `${window.location.origin}/auth/google`;
    // Lightweight CSRF guard — generate, store, verify on callback.
    const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('google_oauth_state', state);
    if (redirectTo) sessionStorage.setItem('sg_post_login_redirect', redirectTo);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      access_type: 'online',
      prompt: 'select_account',
      state,
    });
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Password confirmation validation for registration
    if (!isLogin) {
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        toast.success('Welcome back!');
        if (result.claimed_event_id) {
          toast.success('Your guest schedule was added to your account.');
          navigate(`/schedule/${result.claimed_event_id}?welcome_tour=1`);
        } else {
          navigate(redirectTo || '/');
        }
      } else {
        const result = await register(formData.email, formData.password, formData.name);
        if (result.verification_required && redirectTo && !result.claimed_event_id) {
          toast.success('Account created! Check your email to verify your address.');
          navigate(redirectTo);
        } else if (result.verification_required) {
          setShowVerificationBanner(true);
          if (result.claimed_event_id) {
            setPendingClaimedEventId(result.claimed_event_id);
            toast.success('Account created! Your guest schedule was added — check your email to verify.');
          } else {
            toast.success('Account created! Please check your email to verify your account.');
          }
        } else {
          toast.success('Account created successfully!');
          if (result.claimed_event_id) {
            toast.success('Your guest schedule was added to your account.');
            navigate(`/schedule/${result.claimed_event_id}?welcome_tour=1`);
          } else {
            navigate(redirectTo || '/');
          }
        }
      }
    } catch (error) {
      toast.error(error.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img 
              src="/logo-transparent.png" 
              alt="Schedule Guru" 
              className="h-24 mx-auto"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </Link>
        </div>

        {/* Verification Banner */}
        {showVerificationBanner && (
          <Card className="mb-4 border-green-200 bg-green-50">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Verification Email Sent!</p>
                  <p className="text-sm text-green-700 mt-1">
                    Please check your inbox and click the verification link to activate your account.
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendingVerification}
                      className="text-green-700 border-green-300 hover:bg-green-100"
                    >
                      {resendingVerification ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Mail className="w-3 h-3 mr-1" />
                      )}
                      Resend Email
                    </Button>
                    <Button
                      size="sm"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        if (pendingClaimedEventId) {
                          navigate(`/schedule/${pendingClaimedEventId}?welcome_tour=1`);
                        } else {
                          navigate(redirectTo || '/');
                        }
                      }}
                    >
                      Continue to App
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl font-bold text-slate-800">
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </CardTitle>
            <CardDescription>
              {isLogin ? 'Sign in to manage your tournaments' : 'Get started with Schedule Guru'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email/Password Form */}
            <Button
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              disabled={loading}
              className="w-full h-12 border-slate-300 hover:bg-slate-50 text-slate-700 font-medium"
              data-testid="google-signin-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="m6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              Continue with Google
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-200" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider">
                <span className="bg-white px-3 text-slate-400">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="name"
                      data-testid="name-input"
                      type="text"
                      placeholder="John Doe"
                      className="pl-10 h-12"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required={!isLogin}
                    />
                  </div>
                </div>
              )}
              
              <div>
                <Label htmlFor="email">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="you@example.com"
                    className="pl-10 h-12"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label htmlFor="password">Password</Label>
                  {isLogin && (
                    <Link 
                      to="/forgot-password" 
                      className="text-xs text-[#00247D] hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  )}
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10 h-12"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
              </div>

              {/* Confirm Password - only for registration */}
              {!isLogin && (
                <div>
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="confirmPassword"
                      data-testid="confirm-password-input"
                      type="password"
                      placeholder="••••••••"
                      className={`pl-10 h-12 ${formData.confirmPassword && formData.password !== formData.confirmPassword ? 'border-red-500 focus:ring-red-500' : ''}`}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      required={!isLogin}
                      minLength={6}
                    />
                  </div>
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      Passwords do not match
                    </p>
                  )}
                  {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password.length >= 6 && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      Passwords match
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                data-testid="submit-btn"
                className="w-full h-12 bg-[#00247D] hover:bg-[#001A5C]"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {isLogin ? 'Signing in...' : 'Creating account...'}
                  </>
                ) : (
                  isLogin ? 'Sign In' : 'Create Account'
                )}
              </Button>
            </form>

            <div className="text-center text-sm">
              <span className="text-slate-500">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
              </span>
              <button
                type="button"
                className="text-[#00247D] font-medium hover:underline"
                onClick={() => setIsLogin(!isLogin)}
                data-testid="toggle-auth-mode"
              >
                {isLogin ? 'Sign up' : 'Sign in'}
              </button>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-500 mt-4">
          By continuing, you agree to our{' '}
          <Link to="/terms" className="text-[#00247D] hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/privacy" className="text-[#00247D] hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
